import { Component, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DataService } from '../../../core/services/data.service';
import { 
  Domain, 
  Enterprise, 
  Event, 
  Advertisement,
  Announcement 
} from '../../../core/models/business.model';
import { finalize, forkJoin } from 'rxjs';
import { LocationService } from '../../../core/services/location.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-home2',
  templateUrl: './home2.component.html',
  styleUrls: ['./home2.component.scss']
})

/**
 * Home2 Component
 */
export class Home2Component implements OnInit {
  // Données
  categoriesData: Domain[] = [];
  berlinData: Enterprise[] = [];
  eventsData: Event[] = [];
  adsData: Advertisement[] = [];
  announcement: Announcement | null = null;
  
  // États
  isLoading = true;
  error: string | null = null;
  currentPage = 1;
  isLoadingMore = false;
  hasMoreData = true;

  searchQuery: string = '';
  selectedCity: string = '';
  cities = ['Niamey', 'Zinder', 'Maradi', 'Tahoua', 'Agadez', 'Dosso', 'Diffa', 'Tillabéri'];

  constructor(
    private modalService: NgbModal,
    private dataService: DataService,
    private locationService: LocationService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.loadData();

    // City Select data
    document.getElementById("city-content")?.addEventListener("click", function (e) {
      const input = e.target as HTMLElement;
      const city = document.querySelector('.city') as HTMLElement;
      city.innerText = input.innerText;
    });

    // categories Select data
    document.getElementById("categories-content")?.addEventListener("click", function (e) {
      const input = e.target as HTMLElement;
      const categories = document.querySelector('.categories') as HTMLElement;
      categories.innerText = input.innerText;
    });
  }

  loadEnterprises(page: number = 1) {
    if (this.isLoadingMore || !this.hasMoreData) return;
    
    this.isLoadingMore = true;
    this.dataService.getEnterprises({ 
      page: page,
      featured: true 
    }).subscribe({
      next: (enterprises) => {
        if (enterprises.length === 0) {
          this.hasMoreData = false;
        } else {
          enterprises.forEach(enterprise => {
            this.locationService.calculateEnterpriseDistance(enterprise)
              .subscribe(distance => {
                enterprise.distance = distance;
              });
          });
          
          if (page === 1) {
            this.berlinData = enterprises.slice(1, 10);
          } else {
            this.berlinData = [...this.berlinData, ...enterprises];
          }
          this.currentPage = page;
        }
        this.isLoadingMore = false;
      },
      error: (error) => {
        this.error = 'Erreur lors du chargement des entreprises';
        this.isLoadingMore = false;
        console.error('Erreur:', error);
      }
    });
  }

  loadMore() {
    this.loadEnterprises(this.currentPage + 1);
  }

  private loadData(): void {
    this.isLoading = true;
    
    forkJoin({
      domains: this.dataService.getDomains(),
      events: this.dataService.getEvents(),
      ads: this.dataService.getAdvertisements(),
      announcements: this.dataService.getAnnouncements()
    })
    .pipe(finalize(() => this.isLoading = false))
    .subscribe({
      next: (data) => {
        this.categoriesData = data.domains;
        this.eventsData = data.events;
        this.adsData = data.ads;
        
        if (data.announcements?.length > 0) {
          this.announcement = data.announcements[0];
        }

        // Charger la première page des entreprises
        this.loadEnterprises();
      },
      error: (error) => {
        this.error = 'Erreur lors du chargement des données';
        console.error('Erreur:', error);
      }
    });
  }

  /**
   * Configuration du carousel Berlin (entreprises en vedette)
   */
  berlin = {
    slidesToShow: 3,
    slidesToScroll: 1,
    arrows: true,
    infinite: false,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
        }
      },
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 3,
        }
      }
    ],
    afterChange: (event: any) => {
      const lastSlide = this.berlinData.length - 3;
      if (event.currentSlide >= lastSlide && this.hasMoreData) {
        this.loadMore();
      }
    }
  };

  /**
   * Configuration du carousel des événements
   */
  events = {
    initialSlide: 0,
    slidesPerView: 1,
    spaceBetween: 25,
    centeredSlides: true,
    navigation: true,
    pagination: true,
    loop: true,
    breakpoints: {
      992: {
        slidesPerView: 2,
      }
    }
  };

  /**
   * Configuration du carousel des publicités
   */
  adsConfig = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    arrows: true,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          arrows: false,
          slidesToShow: 1
        }
      }
    ]
  };

  /**
   * Configuration du carousel des blogs
   */
  blogs = {
    initialSlide: 0,
    slidesToShow: 3,
    spaceBetween: 25,
    pagination: true,
    navigation: true,
    loop: true,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 3,
        },
      },
    ],
  };

  selectCity(city: string): void {
    this.selectedCity = city;
  }

  onSearch(): void {
    const queryParams: any = {};
    
    if (this.searchQuery) {
      queryParams.q = this.searchQuery;
    }
    
    if (this.selectedCity) {
      queryParams.city = this.selectedCity;
    }

    this.router.navigate(['/catalog/grid'], { queryParams });
  }
}